import React, { Fragment, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Form } from 'react-bootstrap';
import { PDFViewer, Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import { formatCurrency } from "../utils/currencyHelper"; 
// --- Custom Components ---
import MessageModal from '../Pages/MessageModel';
// --- Action Types ---
import { imgUrl } from 'src/actions/types';
import { getUpiDetails, getBankDetails } from 'src/actions/bankDetailActions';
// --- Redux Actions ---
import { getAllPatientProfiles } from 'src/actions/patientProfileActions';
import { getAllDoctors } from 'src/actions/doctorActions';
import { listDoctorInvoices, updateInvoice } from 'src/actions/invoiceActions';
import { initiatePayout, checkPayoutStatus } from 'src/actions/payoutActions';
import {
    listDoctorPayouts,
    createDoctorPayoutRecord,
    updateDoctorPayoutRecord,
} from 'src/actions/doctorPayoutAction';
import { getPlatformSettings } from 'src/actions/chargesSettingsActions';


// --- Helper Functions ---
const getBadgeClass = (status) => {
    switch (status) {
        case 'Paid': return 'paid-label ';
        case 'Pending': return 'pending-label';
        case 'processing': return 'processing-label';

        case 'Cancelled': return 'failed-label';
        default: return 'failed-label';
    }
};

const getPayoutBadgeClass = (status) => {
    switch (status) {
        case 'completed': case 'processed': return 'complete-label';
        case 'processing': case 'queued': return 'processing-label';
        case 'failed': case 'reversed': return 'failed-label';
        case 'Awaiting': return 'pending-label';
        default: return 'bg-secondary-light';
    }
};

const getDisplayStatus = (status) => {
    switch (status) {
        case 'completed': case 'processed': return 'Success';
        case 'processing': case 'queued': return 'Processing';
        case 'failed': case 'reversed': return 'Failed';
        case 'Awaiting': return 'Pending';
        default: return status;
    }
};

const formatDate = (dateString, options = { year: 'numeric', month: 'short', day: 'numeric' }) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, options);
};

// --- PDF Document Component ---
const pdfStyles = StyleSheet.create({
    page: { backgroundColor: '#FFFFFF', padding: 30, fontFamily: 'Helvetica' },
    header: { 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        borderBottomWidth: 2, 
        borderBottomColor: '#0084d9', // Updated to blue shade
        paddingBottom: 10, 
        marginBottom: 20 
    },
    headerColumn: { width: '48%' },
    title: { 
        fontSize: 24, 
        fontWeight: 'bold', 
        color: '#000', // Updated to blue shade
        marginBottom: 10 
    },
    infoText: { fontSize: 11, color: '#424242', marginBottom: 3 },
    vitalLabel: { fontWeight: 'bold' },
    section: { marginBottom: 20 },
    sectionTitle: { 
        fontSize: 14, 
        fontWeight: 'bold', 
        color: '#000', // Updated to blue shade
        marginBottom: 8 
    },
    pageNumber: { position: 'absolute', fontSize: 10, bottom: 30, left: 0, right: 0, textAlign: 'center', color: 'grey' },
    table: { display: "table", width: "auto", borderStyle: "solid", borderWidth: 1, borderRightWidth: 0, borderBottomWidth: 0 },
    tableRow: { margin: "auto", flexDirection: "row" },
    tableColHeader: { width: "75%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, backgroundColor: '#f0f0f0', padding: 5 },
    tableCol: { width: "75%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 5 },
    tableCellHeader: { fontSize: 10, fontWeight: 'bold' },
    tableCell: { margin: "auto", fontSize: 10 },
    invoiceTotalSection: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 20, borderTopWidth: 1, paddingTop: 10 },
    invoiceTotalLabel: { fontSize: 12, fontWeight: 'bold', width: '75%', textAlign: 'right', paddingRight: 10 },
    invoiceTotalValue: { fontSize: 12, fontWeight: 'bold', width: '25%', textAlign: 'right' },
});
const InvoicePDF = ({ invoice,selectedCurrency  }) => (
    <Document>
        <Page size="A4" style={pdfStyles.page}>
             <View style={pdfStyles.header}>
                <View style={pdfStyles.headerColumn}>
                    <Text style={pdfStyles.title}>Invoice</Text>
                    <Text style={pdfStyles.infoText}><Text style={pdfStyles.vitalLabel}>Invoice ID:</Text> INV-{invoice?.id || 'N/A'}</Text>
                    <Text style={pdfStyles.infoText}><Text style={pdfStyles.vitalLabel}>Date:</Text> {formatDate(invoice?.date)}</Text>
                </View>
                <View style={[pdfStyles.headerColumn, { textAlign: 'right' }]}>
                    <Text style={pdfStyles.infoText}><Text style={pdfStyles.vitalLabel}>Status:</Text> {invoice?.Status || 'N/A'}</Text>
                </View>
            </View>
            <View style={pdfStyles.header}>
                <View style={pdfStyles.headerColumn}>
                    <Text style={pdfStyles.sectionTitle}>Billed To</Text>
                    <Text style={pdfStyles.infoText}>{invoice?.patientDetails?.firstName || ''} {invoice?.patientDetails?.lastName || ''}</Text>
                    <Text style={pdfStyles.infoText}>Patient ID: {invoice?.patientDetails?.PatientID || 'N/A'}</Text>
                </View>
                <View style={[pdfStyles.headerColumn, { textAlign: 'right' }]}>
                    <Text style={pdfStyles.sectionTitle}>Billed By</Text>
                    <Text style={pdfStyles.infoText}>Dr. {invoice?.doctorDetails?.firstName || ''} {invoice?.doctorDetails?.lastName || ''}</Text>
                </View>
            </View>
            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Summary</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableRow}>
                        <View style={pdfStyles.tableColHeader}><Text style={pdfStyles.tableCellHeader}>Description</Text></View>
                        <View style={[pdfStyles.tableColHeader, { width: '25%' }]}><Text style={pdfStyles.tableCellHeader}>Amount</Text></View>
                    </View>
                    <View style={pdfStyles.tableRow}>
                        <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>Doctor Consultation Fee</Text></View>
          <View style={[pdfStyles.tableCol, { width: '25%' }]}>
                    {/* ₹ symbol ki jagah formatCurrency use karein */}
                    <Text style={pdfStyles.tableCell}>{formatCurrency(invoice?.TotalAmount, selectedCurrency)}</Text>
                </View>
        
                    </View>
                </View>
                <View style={pdfStyles.invoiceTotalSection}>
                    <Text style={pdfStyles.invoiceTotalLabel}>Total Amount:</Text>
 <Text style={pdfStyles.invoiceTotalValue}>{formatCurrency(invoice?.TotalAmount, selectedCurrency)}</Text>                </View>
            </View>
            <Text style={pdfStyles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
        </Page>
    </Document>
);


// --- Main Component ---
const DoctorTransactions = () => {
    console.log("[RENDER] DoctorTransactions component is rendering.");

    const dispatch = useDispatch();
    const pollingIntervalRef = useRef(null);
    const activeRecordIdRef = useRef(null);

    // --- Redux State ---
    const { loading: invoicesLoading, error: invoicesError, invoices } = useSelector((state) => state.invoice);
    const { profiles: patients } = useSelector((state) => state.patientProfile);
    const { doctors } = useSelector((state) => state.doctorList);
    const { selectedCurrency } = useSelector((state) => state.currencyReducer);
    const { loading: payoutLoading, error: payoutError, payout } = useSelector((state) => state.payout);
    const { status: payoutStatusResult } = useSelector((state) => state.payoutStatus);
    const { payoutRecords = [] } = useSelector((state) => state.doctorPayout);
    const { settings: platformSettings } = useSelector((state) => state.platformSettings);
    const { upiList } = useSelector((state) => state.upiDetails);
    const { bankList } = useSelector((state) => state.bankDetails);
    
    // --- Local State for UI Control ---
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [invoiceToEdit, setInvoiceToEdit] = useState(null);
    const [editAmount, setEditAmount] = useState('');
    const [editStatus, setEditStatus] = useState('Pending');
    const [activeProcess, setActiveProcess] = useState({ type: null, id: null });
    const [messageModal, setMessageModal] = useState({ show: false, title: '', message: '', variant: 'info' });
    const [countdown, setCountdown] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
const [itemsPerPage, setItemsPerPage] = useState(10);

    // --- Core Logic & Effects ---
    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown(prevCountdown => prevCountdown > 0 ? prevCountdown - 1 : 0);
            }, 1000);
        } else if (countdown === 0 && activeProcess.type === 'single') {
            setActiveProcess({ type: null, id: null });
        }
        return () => clearInterval(timer);
    }, [countdown, activeProcess.type]);

    useEffect(() => {
        const handlePayoutResponse = async () => {
            if (payoutLoading) return;
    
            const recordIdToUpdate = activeRecordIdRef.current;
    
            const cleanup = () => {
                stopPolling();
                activeRecordIdRef.current = null;
                setActiveProcess({ type: null, id: null });
                setCountdown(0);
            };
    
            if (payoutError) {
                setMessageModal({ show: true, title: 'Payout Error', message: `Payout Initiation Error: ${payoutError}`, variant: 'danger' });
                if (recordIdToUpdate) {
                    await dispatch(updateDoctorPayoutRecord(recordIdToUpdate, { paymentStatus: 'failed' }));
                    dispatch(listDoctorPayouts());
                }
                cleanup();
                return;
            }
            
            if (!payout || !payout.payoutDetails || !recordIdToUpdate) {
                return;
            }
    
            const { status, payoutId, gateway_created_at, initiated_at } = payout.payoutDetails;
            const dateFromGateway = gateway_created_at || initiated_at;

            const updatePayload = { 
                payoutId, 
                paymentStatus: status,
                date: dateFromGateway 
            };

            await dispatch(updateDoctorPayoutRecord(recordIdToUpdate, updatePayload));
            dispatch(listDoctorPayouts()); 

            if (status === 'processing' || status === 'queued') {
                let attempts = 0;
                pollingIntervalRef.current = setInterval(() => {
                    if (attempts >= 6) { 
                        stopPolling();
                        return;
                    }
                    dispatch(checkPayoutStatus(payoutId));
                    attempts++;
                }, 10000);
            } else {
                cleanup();
            }
        };
    
        handlePayoutResponse();
        return () => { if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current); };
    }, [payout, payoutError, payoutLoading, dispatch, stopPolling]);
    
    useEffect(() => {
        const handleStatusUpdate = async () => {
            const payoutObject = payoutStatusResult?.data?.[0];
            if (!payoutObject) return;
    
            const payoutId = payoutObject.payout_id;
            const recordToUpdate = payoutRecords.find(r => (r.payoutId || r.payoutid) === payoutId);
            
            if (!recordToUpdate) return;
            
            const currentStatus = payoutObject.status;
            const hasStatusChanged = recordToUpdate.paymentStatus !== currentStatus;
            
            const isDateMissing = !recordToUpdate.date;
            const dateFromGateway = payoutObject.gateway_created_at || payoutObject.initiated_at;

            if (hasStatusChanged || (isDateMissing && dateFromGateway)) {
                
                const updatePayload = { 
                    paymentStatus: currentStatus,
                    date: dateFromGateway
                };
    
                await dispatch(updateDoctorPayoutRecord(recordToUpdate.id, updatePayload));
                dispatch(listDoctorPayouts());
    
                const isFinalStatus = ['processed', 'completed', 'failed', 'reversed'].includes(currentStatus);
                if (isFinalStatus) {
                    stopPolling();
                    setActiveProcess({ type: null, id: null });
                    setCountdown(0);
                }
    
                if (hasStatusChanged) {
                    setMessageModal({
                        show: true,
                        title: 'Status Synced',
                        message: `The payout status has been updated to '${currentStatus}'.`,
                        variant: isFinalStatus ? 'success' : 'info'
                    });
                }
            } else {
                const isManualCheck = activeProcess.type === 'checking' && activeProcess.id === payoutId;
                if (isManualCheck) {
                    setMessageModal({ show: true, title: 'Status Check', message: `The status is still '${currentStatus}'.`, variant: 'info' });
                    setActiveProcess({ type: null, id: null });
                }
            }
        };
    
        handleStatusUpdate();
    }, [payoutStatusResult, payoutRecords, dispatch, stopPolling, activeProcess]);
    const hospitalID = parseInt(localStorage.getItem('hospitalCode'), 10);

    // useEffect(() => {
    //     console.log("[EFFECT] Fetching initial data...");
        
    //     dispatch(listDoctorInvoices());
    //     dispatch(getAllPatientProfiles());
    //     dispatch(getAllDoctors(hospitalID));
    //     dispatch(listDoctorPayouts());
    //     dispatch(getPlatformSettings(2));
    // }, [dispatch]);

    // --- Data Enrichment and Calculation Logic ---
    // DoctorTransactions.js file mein useEffect ke andar jayein
useEffect(() => {
    console.log("[EFFECT] Fetching initial data...");
    
    if (hospitalID) {
        dispatch(getAllDoctors({ hospitalID })); 
        dispatch(getUpiDetails(hospitalID)); // Fetch UPI details
        dispatch(getBankDetails(hospitalID)); // Fetch Bank details
    }

    dispatch(listDoctorInvoices());
    dispatch(getAllPatientProfiles());
    dispatch(listDoctorPayouts());
    dispatch(getPlatformSettings(2));
}, [dispatch, hospitalID]);
useEffect(() => {
    console.log("[EFFECT] Fetching initial data...");
    
    const hospitalID = parseInt(localStorage.getItem('hospitalCode'), 10);

  
    if (hospitalID) {
        dispatch(getAllDoctors({ hospitalID })); 
    }

    dispatch(listDoctorInvoices());
    dispatch(getAllPatientProfiles());
    dispatch(listDoctorPayouts());
    dispatch(getPlatformSettings(2));
}, [dispatch]); 

    const enrichedInvoices = useMemo(() => {
        console.log("[MEMO] Starting invoice enrichment and calculation...");
        if (!invoices || !platformSettings) {
            console.log("[MEMO] Aborting: Missing invoices or platform settings.");
            return [];
        }

        const patientMap = new Map(patients.map(p => [p.PatientID, p]));
        const doctorMap = new Map(doctors.map(d => [d.hospitalID, d]));
        const payoutRecordMap = new Map(payoutRecords.map(r => [String(r.invoiceId || r.InvoiceId), r]));

        const sortedInvoices = [...invoices].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        console.log("[MEMO] Platform Settings:", platformSettings);
        let pendingPlatformCharges = 0;

        const calculatedData = sortedInvoices.map(invoice => {
            const isPaidAtClinic = invoice.TransactionId && invoice.TransactionId.startsWith('clinic_');
            const isPaidOnline = !isPaidAtClinic;
            const appointmentFee = parseFloat(invoice.TotalAmount || 0);
            
            let platformChargeForThisTxn = 0;
            let payableAmount = 0;

            console.log(`\n[MEMO] Processing Invoice #${invoice.id} | Fee: ${appointmentFee} | Type: ${isPaidAtClinic ? 'Clinic' : 'Online'}`);
            console.log(`[MEMO]  - Before processing, pending charges: ${pendingPlatformCharges}`);

            if (invoice.Status === 'Paid') {
                const platformChargePercent = parseFloat(platformSettings.platformCharge || 0);
                platformChargeForThisTxn = (appointmentFee * platformChargePercent) / 100;
                
                if (isPaidAtClinic) {
                    pendingPlatformCharges += platformChargeForThisTxn;
                    console.log(`[MEMO]  - Clinic payment. Adding ${platformChargeForThisTxn} to pending. New pending total: ${pendingPlatformCharges}`);
                }
            }
            
            if (invoice.Status === 'Paid' && isPaidOnline) {
                const payinChargePercent = parseFloat(platformSettings.payinCharge || 0);
                const payoutChargePercent = parseFloat(platformSettings.payoutCharge || 0);
                const payinCharge = (appointmentFee * payinChargePercent) / 100;
                const payoutCharge = (appointmentFee * payoutChargePercent) / 100;
                let totalDeductions = payinCharge + payoutCharge + platformChargeForThisTxn;
                console.log(`[MEMO]  - Online payment. Deductions for this txn: payin(${payinCharge}) + payout(${payoutCharge}) + platform(${platformChargeForThisTxn}) = ${totalDeductions}`);

                if (pendingPlatformCharges > 0) {
                    const remainingAmountAfterDeductions = appointmentFee - totalDeductions;
                    const amountToClear = Math.min(pendingPlatformCharges, remainingAmountAfterDeductions);
                    
                    if (amountToClear > 0) {
                        console.log(`[MEMO]  - Clearing ${amountToClear} of pending charges.`);
                        totalDeductions += amountToClear;
                        pendingPlatformCharges -= amountToClear;
                    }
                }
                
                payableAmount = appointmentFee - totalDeductions;
                
                console.log(`[MEMO]  - Final payable: ${appointmentFee} - ${totalDeductions} = ${payableAmount}`);
                console.log(`[MEMO]  - After processing, remaining pending charges: ${pendingPlatformCharges}`);
            }

            const payoutRecord = payoutRecordMap.get(String(invoice.id));
            return {
                ...invoice,
                patientDetails: patientMap.get(invoice.PatientID) || {},
                doctorDetails: doctorMap.get(invoice.hospitalID) || {},
                payoutStatus: payoutRecord ? payoutRecord.paymentStatus : 'Awaiting',
                payoutId: payoutRecord ? (payoutRecord.payoutId || payoutRecord.payoutid) : null,
                payoutRecordId: payoutRecord ? payoutRecord.id : null,
                appointmentFee: appointmentFee,
                platformCharge: platformChargeForThisTxn,
                payableAmount: payableAmount,
            };
        });
        
        console.log("[MEMO] Calculation complete. Reversing array for display.");
        return calculatedData.reverse();
    }, [invoices, patients, doctors, payoutRecords, platformSettings]);
    const indexOfLastItem = currentPage * itemsPerPage;
const indexOfFirstItem = indexOfLastItem - itemsPerPage;
const currentItems = enrichedInvoices.slice(indexOfFirstItem, indexOfLastItem);
const totalPages = Math.ceil(enrichedInvoices.length / itemsPerPage);
useEffect(() => {
    setCurrentPage(1);
}, [itemsPerPage]);
    // const handleSingleTransfer = async (invoice) => {
    //     if (activeProcess.type) return;
    //     setActiveProcess({ type: 'single', id: invoice.id });
    //     setCountdown(60);
    
    //     const payoutRecordPayload = {
    //         hospitalID: String(invoice.hospitalID),
    //         invoiceId: String(invoice.id),
    //         Amount: "1",
    //         paymentStatus: 'processing',
    //         payoutId: null,
    //         date: null 
    //     };
    
    //     try {
    //         const newRecord = await dispatch(createDoctorPayoutRecord(payoutRecordPayload));
    //         if (newRecord && newRecord.id) {
    //             activeRecordIdRef.current = newRecord.id;
    //             dispatch(listDoctorPayouts());
    //             const gatewayPayload = {    "gateway": "cashfree",
    // "hospitalID": "",
    // "amount": "",
    // "currency": "",
    // "payment_mode": "", 
    // "account_number": "",
    // "ifsc": ""};
    //             dispatch(initiatePayout(gatewayPayload));
    //         } else {
    //             throw new Error("Failed to create payout record in the database.");
    //         }
    //     } catch (err) {
    //         setMessageModal({ show: true, title: 'Error', message: err.message || "Could not start the transfer process.", variant: 'danger' });
    //         setActiveProcess({ type: null, id: null });
    //         setCountdown(0);
    //         dispatch(listDoctorPayouts());
    //     }
    // };
//     const handleSingleTransfer = async (invoice) => {
//     if (activeProcess.type) return;

//     // --- Find Primary Payment Method ---
//     let primaryMethod = null;
//     let paymentData = {
//         mode: "",
//         account: "",
//         ifsc: ""
//     };

//     // Check Bank List for primary
//     const primaryBank = bankList?.find(b => b.is_primary);
//     // Check UPI List for primary
//     const primaryUpi = upiList?.find(u => u.is_primary);

//     if (primaryBank) {
//         paymentData.mode = "banktransfer";
//         paymentData.account = primaryBank.account_number;
//         paymentData.ifsc = primaryBank.ifsc_code;
//     } else if (primaryUpi) {
//         paymentData.mode = "upi";
//         paymentData.account = primaryUpi.vpa; // For UPI, we pass VPA in the account field or specific vpa field
//         paymentData.ifsc = ""; 
//     } else {
//         // Fallback: If no primary is selected, show error
//         setMessageModal({ 
//             show: true, 
//             title: 'Missing Payment Info', 
//             message: "No primary payout method found. Please set a primary Bank or UPI account in Profile Settings.", 
//             variant: 'warning' 
//         });
//         return;
//     }

//     setActiveProcess({ type: 'single', id: invoice.id });
//     setCountdown(60);

//     const payoutRecordPayload = {
//         hospitalID: String(invoice.hospitalID),
//         invoiceId: String(invoice.id),
//         Amount: String(invoice.payableAmount || 1), // Using actual payable amount
//         paymentStatus: 'processing',
//         payoutId: null,
//         date: null 
//     };

//     try {
//         const newRecord = await dispatch(createDoctorPayoutRecord(payoutRecordPayload));
//         if (newRecord && newRecord.id) {
//             activeRecordIdRef.current = newRecord.id;
//             dispatch(listDoctorPayouts());

//             // --- Dynamic Gateway Payload ---
//             const gatewayPayload = {
//                 "gateway": "cashfree",
//                 "hospitalID": String(hospitalID),
//                 "amount": String(invoice.payableAmount || 1),
//                 "currency": selectedCurrency?.currency || "INR",
//                 "payment_mode": paymentData.mode, 
//                 // "account_number": paymentData.account,
//                 // "ifsc": paymentData.ifsc
//                 "vpa": "janavikurkure-2@okaxis"
//             };

//             dispatch(initiatePayout(gatewayPayload));
//         } else {
//             throw new Error("Failed to create payout record.");
//         }
//     } catch (err) {
//         setMessageModal({ show: true, title: 'Error', message: err.message || "Could not start transfer.", variant: 'danger' });
//         setActiveProcess({ type: null, id: null });
//         setCountdown(0);
//         dispatch(listDoctorPayouts());
//     }
// };
const handleSingleTransfer = async (invoice) => {
    if (activeProcess.type) return;

    // --- 1. Identify Primary Payment Method ---
    const primaryBank = bankList?.find(b => b.is_primary || b.Is_primary);
    const primaryUpi = upiList?.find(u => u.is_primary || u.Is_primary);

    let gatewayPayload = {
        "gateway": "cashfree",
        "hospitalID": String(hospitalID),
        "amount": String(invoice.payableAmount || 1),
        "currency": selectedCurrency?.currency || "INR"
    };

    // --- 2. Construct Payload based on Method ---
    if (primaryBank) {
        gatewayPayload = {
            ...gatewayPayload,
            "payment_mode": "banktransfer",
            "account_number": primaryBank.account_number,
            "ifsc": primaryBank.ifsc_code
        };
    } else if (primaryUpi) {
        gatewayPayload = {
            ...gatewayPayload,
            "payment_mode": "upi",
            // Dynamically get UPI ID from the record
            "vpa": primaryUpi.vpa || primaryUpi.upiId 
        };
    } else {
        // Fallback: If no primary is selected, stop and alert user
        setMessageModal({ 
            show: true, 
            title: 'Missing Payment Info', 
            message: "No primary payout method found. Please set a primary Bank or UPI account in Profile Settings.", 
            variant: 'warning' 
        });
        return;
    }

    // --- 3. Execute Process ---
    setActiveProcess({ type: 'single', id: invoice.id });
    setCountdown(60);

    const payoutRecordPayload = {
        hospitalID: String(invoice.hospitalID),
        invoiceId: String(invoice.id),
        Amount: String(invoice.payableAmount || 1),
        paymentStatus: 'processing',
        payoutId: null,
        date: null 
    };

    try {
        const newRecord = await dispatch(createDoctorPayoutRecord(payoutRecordPayload));
        if (newRecord && newRecord.id) {
            activeRecordIdRef.current = newRecord.id;
            dispatch(listDoctorPayouts());
            
            // Dispatch with the dynamic payload constructed above
            dispatch(initiatePayout(gatewayPayload));
        } else {
            throw new Error("Failed to create payout record.");
        }
    } catch (err) {
        setMessageModal({ show: true, title: 'Error', message: err.message || "Could not start transfer.", variant: 'danger' });
        setActiveProcess({ type: null, id: null });
        setCountdown(0);
        dispatch(listDoctorPayouts());
    }
};
    // const handleRetryInitiation = (invoice) => {
    //     if (activeProcess.type) return;
    //     activeRecordIdRef.current = invoice.payoutRecordId;
    //     setActiveProcess({ type: 'single', id: invoice.id });
    //     setCountdown(60);
    //     const gatewayPayload = { hospitalID: invoice.hospitalID, amount: 1, currency: "INR", gateway: "razorpay" };
    //     dispatch(initiatePayout(gatewayPayload));
    // };
//    const handleRetryInitiation = (invoice) => {
//     if (activeProcess.type) return;

//     // Find primary method exactly like in handleSingleTransfer
//     const primaryBank = bankList?.find(b => b.is_primary);
//     const primaryUpi = upiList?.find(u => u.is_primary);

//     let paymentData = { mode: "", account: "", ifsc: "" };
//     if (primaryBank) {
//         paymentData = { mode: "banktransfer", account: primaryBank.account_number, ifsc: primaryBank.ifsc_code };
//     } else if (primaryUpi) {
//         paymentData = { mode: "upi", account: primaryUpi.vpa, ifsc: "" };
//     }

//     activeRecordIdRef.current = invoice.payoutRecordId;
//     setActiveProcess({ type: 'single', id: invoice.id });
//     setCountdown(60);

//     const gatewayPayload = { 
//         "gateway": "cashfree",
//         "hospitalID": String(hospitalID), 
//         "amount": String(invoice.payableAmount || 1), 
//         "currency": selectedCurrency?.currency || "INR",
//         "payment_mode": paymentData.mode,
//         "account_number": paymentData.account,
//         "ifsc": paymentData.ifsc
//     };
//     dispatch(initiatePayout(gatewayPayload));
// }; 
const handleRetryInitiation = (invoice) => {
    if (activeProcess.type) return;

    const primaryBank = bankList?.find(b => b.is_primary || b.Is_primary);
    const primaryUpi = upiList?.find(u => u.is_primary || u.Is_primary);

    let gatewayPayload = {
        "gateway": "cashfree",
        "hospitalID": String(hospitalID),
        "amount": String(invoice.payableAmount || 1),
        "currency": selectedCurrency?.currency || "INR"
    };

    if (primaryBank) {
        gatewayPayload = {
            ...gatewayPayload,
            "payment_mode": "banktransfer",
            "account_number": primaryBank.account_number,
            "ifsc": primaryBank.ifsc_code
        };
    } else if (primaryUpi) {
        gatewayPayload = {
            ...gatewayPayload,
            "payment_mode": "upi",
            "vpa": primaryUpi.vpa || primaryUpi.upiId 
        };
    } else {
        setMessageModal({ show: true, title: 'Error', message: "No primary payment method found for retry.", variant: 'danger' });
        return;
    }

    activeRecordIdRef.current = invoice.payoutRecordId;
    setActiveProcess({ type: 'single', id: invoice.id });
    setCountdown(60);
    dispatch(initiatePayout(gatewayPayload));
};
 const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
};

const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
};

// const renderPagination = () => {
//     const pageNumbers = [];
//     for (let i = 1; i <= totalPages; i++) {
//         pageNumbers.push(i);
//     }
//   return (
//         <div className="row mt-4">
//             <div className="col-sm-12 col-md-5">
//                 <div className="dataTables_info" id="DataTables_Table_0_info" role="status" aria-live="polite">
//                     Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, enrichedInvoices.length)} of {enrichedInvoices.length} entries
//                 </div>
//             </div>
//             <div className="col-sm-12 col-md-7">
//                 <div className="dataTables_paginate paging_simple_numbers" id="DataTables_Table_0_paginate">
//                     <ul className="pagination">
//                         <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`} id="DataTables_Table_0_previous">
//                             <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage > 1) handlePageChange(currentPage - 1); }} className="page-link">
//                                 Previous
//                             </a>
//                         </li>
//                         {pageNumbers.map(number => (
//                             <li key={number} className={`paginate_button page-item ${currentPage === number ? 'active' : ''}`}>
//                                 <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(number); }} className="page-link">
//                                     {number}
//                                 </a>
//                             </li>
//                         ))}
//                         <li className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`} id="DataTables_Table_0_next">
//                             <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) handlePageChange(currentPage + 1); }} className="page-link">
//                                 Next
//                             </a>
//                         </li>
//                     </ul>
//                 </div>
//             </div>
//         </div>
//     );
// };   
  
const renderPagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5; // Number of page buttons to show
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    return (
        <div className="dataTables_paginate paging_simple_numbers">
            <ul className="pagination">
                <div className="dataTables_info mr-3 mt-2">
                                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, enrichedInvoices.length)} of {enrichedInvoices.length} entries
                                    </div>
                <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                    
                    <a 
                        href="#" 
                        onClick={(e) => { 
                            e.preventDefault(); 
                            if (currentPage > 1) handlePageChange(currentPage - 1); 
                        }} 
                        className="page-link"
                    >
                        Previous
                    </a>
                </li>
                
                {startPage > 1 && (
                    <>
                        <li className="paginate_button page-item">
                            <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(1); }} className="page-link">1</a>
                        </li>
                        {startPage > 2 && (
                            <li className="paginate_button page-item disabled">
                                <span className="page-link">...</span>
                            </li>
                        )}
                    </>
                )}

                {pageNumbers.map(number => (
                    <li key={number} className={`paginate_button page-item ${currentPage === number ? 'active' : ''}`}>
                        <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(number); }} className="page-link">
                            {number}
                        </a>
                    </li>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && (
                            <li className="paginate_button page-item disabled">
                                <span className="page-link">...</span>
                            </li>
                        )}
                        <li className="paginate_button page-item">
                            <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(totalPages); }} className="page-link">{totalPages}</a>
                        </li>
                    </>
                )}

                <li className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <a 
                        href="#" 
                        onClick={(e) => { 
                            e.preventDefault(); 
                            if (currentPage < totalPages) handlePageChange(currentPage + 1); 
                        }} 
                        className="page-link"
                    >
                        Next
                    </a>
                </li>
            </ul>
        </div>
    );
};
const handleManualStatusCheck = (payoutId) => {
        if (activeProcess.type) return;
        if (!payoutId) return;
        setActiveProcess({ type: 'checking', id: payoutId });
        dispatch(checkPayoutStatus(payoutId));
    };

    const handleViewClick = (invoice) => {
        setSelectedInvoice(invoice);
        setShowPdfModal(true);
    };

    const handleEditClick = (invoice) => {
        setInvoiceToEdit(invoice);
        setEditAmount(invoice.TotalAmount);
        setEditStatus(invoice.Status);
        setShowEditModal(true);
    };
    
    const handleCloseModals = () => {
        setShowPdfModal(false);
        setSelectedInvoice(null);
        setShowEditModal(false);
        setInvoiceToEdit(null);
    };
//     const renderItemsPerPageSelector = () => {
//     return (
//         <div className="dataTables_length" id="DataTables_Table_0_length">
//             <label>
//                 Items: 
//                 <select 
//                     name="DataTables_Table_0_length" 
//                     aria-controls="DataTables_Table_0" 
//                     className="form-control form-control-sm ml-2"
//                     value={itemsPerPage}
//                     onChange={handleItemsPerPageChange}
//                     style={{ width: 'auto', display: 'inline-block' }}
//                 >
//                     <option value="5">5</option>
//                     <option value="10">10</option>
//                     <option value="20">20</option>
//                     <option value="50">50</option>
//                 </select>
//             </label>
//         </div>
//     );
// };
const renderItemsPerPageSelector = () => {
    return (
        <div className="dataTables_length">
            <label>
                Items: 
                <select 
                    name="items_per_page" 
                    className="form-control form-control-sm ml-2"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    style={{ width: '70px', display: 'inline-block', marginLeft: '10px' }}
                >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                </select>
            </label>
        </div>
    );
};   
const handleSaveChanges = async () => {
        if (!invoiceToEdit) return;
        const updatePayload = { TotalAmount: parseFloat(editAmount || 0), Status: editStatus };
        await dispatch(updateInvoice(invoiceToEdit.id, updatePayload));
        setMessageModal({ show: true, title: 'Success', message: `Invoice #${invoiceToEdit.id} updated successfully!`, variant: 'success' });
        handleCloseModals();
        dispatch(listDoctorInvoices());
    };

    return (
       <Fragment>
        <style>{`
            .transactions-container {
                margin-left: 0;
                
                transition: margin-left 0.3s ease;
            }
            
            /* If you have a sidebar class, adjust based on its state */
            .sidebar-open .transactions-container {
              /* Adjust based on your sidebar width */
            }
            
            /* Pagination container styling */
            .pagination-wrapper {
                display: flex;
                justify-content: space-between;
                align-items: center;
                flex-wrap: wrap;
                margin-top: 20px;
                padding: 15px 0;
                border-top: 1px solid #dee2e6;
            }
            
            .items-per-page-wrapper {
                flex: 0 0 auto;
            }
            
            .pagination-controls-wrapper {
                flex: 1 1 auto;
                display: flex;
                justify-content: flex-end;
            }
            
            .dataTables_length {
                margin-bottom: 0;
            }
            
            .dataTables_length label {
                display: inline-flex;
                align-items: center;
                font-weight: normal;
                margin-bottom: 0;
            }
            
            .dataTables_length select {
                margin-left: 10px;
                width: 70px;
                border-radius: 4px;
                border: 1px solid #ddd;
                padding: 5px;
            }
            
            .pagination {
                margin: 0;
                padding: 0;
                float: right;
            }
            
            .pagination .page-item.active .page-link {
                background-color: #0084d9;
                border-color: #0084d9;
                color: white;
            }
            
            .pagination .page-link {
                color: #0084d9;
                padding: 0.5rem 0.75rem;
                border-radius: 4px;
                margin: 0 2px;
            }
            
            .pagination .page-item.disabled .page-link {
                color: #6c757d;
                pointer-events: none;
                cursor: auto;
                background-color: #fff;
                border-color: #dee2e6;
            }
            
            .dataTables_info {
                
                font-size: 0.9rem;
                color: #6c757d;
                flex: 1 1 100%;
                text-align: center;
              
            }
            
            /* Responsive adjustments */
            @media (max-width: 768px) {
                .pagination-wrapper {
                    flex-direction: column;
                    align-items: flex-start;
                }
                
                .pagination-controls-wrapper {
                    justify-content: flex-start;
                    margin-top: 15px;
                    width: 100%;
                    overflow-x: auto;
                }
                
                .pagination {
                    float: left;
                }
            }
        `}</style>
        
        <div className="transactions-container">
            <div className="filter-bar page-header rounded">
                <div className="row align-items-center">
                    <div className="col">
                        <h3 className="page-title">My Transactions</h3>
                        <ul className="breadcrumb">
                            <li className="breadcrumb-item"><Link to="/doctor/dashboard">Doctor Dashboard</Link></li>
                            <li className="breadcrumb-item active">Transactions</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            {/* Table Header with Items Per Page */}
                            

                            {/* Table */}
                            <div className="table-responsive">
                                <table className="datatable table table-hover table-center mb-0">
                                    <thead>
                                        <tr>
                                            <th>Invoice ID</th>
                                            <th>Patient</th>
                                            <th>Appt Fee</th>
                                            <th>Platform Charge</th>
                                            <th>Payable Amount</th>
                                            <th>Payment Status</th>
                                            <th>Transfer Status</th>
                                            <th className="text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoicesLoading ? ( 
                                            <tr><td colSpan="8" className="text-center">Loading...</td></tr>
                                        ) : invoicesError ? ( 
                                            <tr><td colSpan="8" className="text-center text-danger">Error: {invoicesError}</td></tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((item) => {
                                                const wasPaidOnline = item.TransactionId && !item.TransactionId.startsWith('clinic_');
                                                const isAnyProcessActive = !!activeProcess.type;
                                                const isThisItemActive = activeProcess.id === item.id;
                                                
                                                const renderActionButtons = () => {
                                                    const markPaidButton = item.Status === 'Pending' && (
                                                        <button
                                                            className="i-btn-mark-paid" 
                                                            onClick={() => handleEditClick(item)}
                                                            disabled={isAnyProcessActive}
                                                            title='Mark Paid'
                                                        ></button>
                                                    );

                                                    if (!wasPaidOnline) return markPaidButton;

                                                    const isCheckingThis = activeProcess.type === 'checking' && activeProcess.id === item.payoutId;
                                                    
                                                    if (isThisItemActive && (activeProcess.type === 'single')) {
                                                        return <button className="btn btn-primary rounded-pill sm" disabled><span className="spinner-border spinner-border-sm mr-1"/>Processing... ({countdown}s)</button>;
                                                    }
                                                    
                                                    let payoutButton = null;
                                                    switch (item.payoutStatus) {
                                                        case 'processed': case 'completed': break;
                                                        case 'processing': case 'queued':
                                                            if (item.payoutId) {
                                                                payoutButton = <button className="i-btn-retry-init" onClick={() => handleRetryInitiation(item)} disabled={isAnyProcessActive} title="Retry Initiation"></button>;
                                                            }
                                                            break;
                                                        case 'failed': case 'reversed':
                                                            payoutButton = <button className="i-btn-transfer-money-retry" onClick={() => handleSingleTransfer(item)} disabled={isAnyProcessActive} title="Retry Transfer"> </button>;
                                                            break;
                                                        default: // Awaiting
                                                            payoutButton = <button className='i-btn-transfer-money' onClick={() => handleSingleTransfer(item)} disabled={isAnyProcessActive} title="Transfer"></button>;
                                                    }
                                                    return <>{markPaidButton}{payoutButton}</>;
                                                };
                                                
                                                return (
                                                    <tr key={item.id} style={{backgroundColor: isThisItemActive ? '#f0f8ff' : 'transparent'}}>
                                                        <td><a href="#!" onClick={(e) => { e.preventDefault(); handleViewClick(item); }}>#{item.id}</a></td>
                                                        <td>
                                                            <h2 className="table-avatar">
                                                                <Link to={`/patient-profile/${item.PatientID}`} className="avatar avatar-sm mr-2"> 
                                                                    <img className="avatar-img rounded-circle" src={item.patientDetails.uploadPhoto ? `${imgUrl}${item.patientDetails.uploadPhoto}` : '/assets/img/patients/patient.jpg'} alt="Patient" /> 
                                                                </Link>
                                                                <Link to={`/patient-profile/${item.PatientID}`}>{item.patientDetails.firstName || 'Unknown'} {item.patientDetails.lastName || ''}</Link>
                                                            </h2>
                                                        </td>
                                                        <td>{formatCurrency(item.appointmentFee, selectedCurrency)}</td>
                                                        <td>{formatCurrency(item.platformCharge, selectedCurrency)}</td>
                                                        <td>
                                                            {wasPaidOnline && item.Status === 'Paid' ? 
                                                                <strong>{formatCurrency(item.payableAmount, selectedCurrency)}</strong> : 
                                                                <span className="text-muted">N/A</span>
                                                            }
                                                        </td>
                                                        <td><span className={`badge badge-pill ${getBadgeClass(item.Status)}`}>{item.Status}</span></td>
                                                        <td>
                                                            {wasPaidOnline ? ( 
                                                                <span className={`badge badge-pill ${getPayoutBadgeClass(item.payoutStatus)}`}> 
                                                                    {getDisplayStatus(item.payoutStatus)} 
                                                                </span>
                                                            ) : ( 
                                                                <span className="badge badge-pill draft-label">N/A (Clinic)</span> 
                                                            )}
                                                        </td>
                                                        <td className="text-right"> 
                                                            <div className="actions"> 
                                                                <button variant="light" size="sm" className="i-btn-view" onClick={() =>{ handleViewClick(item); }} title='View Details'> </button> 
                                                                {renderActionButtons()} 
                                                            </div> 
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : ( 
                                            <tr><td colSpan="8" className="text-center">No transactions found.</td></tr> 
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Section */}
                            {enrichedInvoices.length > 0 && (
                                <div className="pagination-wrapper">
                                    <div className="items-per-page-wrapper">
                                        {renderItemsPerPageSelector()}
                                    </div>
                                    <div className="pagination-controls-wrapper">
                                        {renderPagination()}
                                    </div>
                                    
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <Modal show={showPdfModal} onHide={handleCloseModals} size="xl" centered>
                <Modal.Header className='bg-info text-white' closeButton closeVariant="white">
                    <Modal.Title>Invoice Viewer: #{selectedInvoice?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ height: '80vh' }}>
                    {showPdfModal && selectedInvoice && (
                        <PDFViewer width="100%" height="100%">
                            <InvoicePDF invoice={selectedInvoice} selectedCurrency={selectedCurrency} />
                        </PDFViewer>
                    )}
                </Modal.Body>
            </Modal>
            
            {invoiceToEdit && (
                <Modal show={showEditModal} onHide={handleCloseModals} centered>
                    <Modal.Header className='bg-info text-white' closeButton closeVariant="white"> 
                        <Modal.Title>Update Invoice #{invoiceToEdit.id}</Modal.Title> 
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group controlId="editAmount">
                                <Form.Label>Total Amount Paid (₹)</Form.Label>
                                <Form.Control type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} />
                            </Form.Group>
                            <Form.Group controlId="editStatus" className="mt-3">
                                <Form.Label>New Status</Form.Label>
                                <Form.Control as="select" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                                    <option value="Pending">Pending</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Cancelled">Cancelled</option>
                                </Form.Control>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-secondary rounded-pill" onClick={handleCloseModals} disabled={invoicesLoading}>Cancel</button>
                        <button className="btn btn-info rounded-pill" onClick={handleSaveChanges} disabled={invoicesLoading}>
                            {invoicesLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </Modal.Footer>
                </Modal>
            )}
            
            <MessageModal 
                show={messageModal.show} 
                onClose={() => setMessageModal({ ...messageModal, show: false })} 
                {...messageModal} 
            />
        </div>
    </Fragment>
    );
}

export default DoctorTransactions;